import i18n from "@/i18n";
import { debug } from "@/mixins/devUtil";
import { sentryClient } from "@/sentry";
import { PUBLIC_CONFIG_DEFAULTS } from "@/utils/configDefaults";
import { useAuthStore } from "@/stores/auth";
import { useToastStore } from "@/stores/toast";

interface ErrorResult {
  summary: string;
  detail?: string;
  params?: Record<string, unknown>;
}

interface FormatErrorOptions {
  resource?: string;
}

const AUTH_ERROR_MAP: Record<string, ErrorResult> = {
  "auth/user-not-found": {
    summary: "error.auth.invalid_credentials.summary",
    detail: "error.auth.invalid_credentials.detail",
  },
  "auth/wrong-password": {
    summary: "error.auth.invalid_credentials.summary",
    detail: "error.auth.invalid_credentials.detail",
  },
  "auth/invalid-credential": {
    summary: "error.auth.invalid_credentials.summary",
    detail: "error.auth.invalid_credentials.detail",
  },
  "auth/too-many-requests": {
    summary: "error.auth.too_many_requests.summary",
    detail: "error.auth.too_many_requests.detail",
  },
  "auth/network-request-failed": {
    summary: "error.auth.network_error.summary",
    detail: "error.auth.network_error.detail",
  },
  "auth/user-disabled": {
    summary: "error.auth.user_disabled.summary",
    detail: "error.auth.user_disabled.detail",
  },
  "auth/invalid-email": {
    summary: "error.auth.invalid_email.summary",
    detail: "error.auth.invalid_email.detail",
  },
  "auth/requires-recent-login": {
    summary: "error.auth.requires_relogin.summary",
    detail: "error.auth.requires_relogin.detail",
  },
  "auth/email-already-in-use": {
    summary: "error.auth.email_in_use.summary",
    detail: "error.auth.email_in_use.detail",
  },
  "auth/weak-password": {
    summary: "error.auth.weak_password.summary",
    detail: "error.auth.weak_password.detail",
  },
};

const FUNCTIONS_ERROR_MAP: Record<string, ErrorResult> = {
  "functions/permission-denied": {
    summary: "error.functions.permission_denied.summary",
    detail: "error.functions.permission_denied.detail",
  },
  "functions/invalid-argument": {
    summary: "error.functions.invalid_argument.summary",
    detail: "error.functions.invalid_argument.detail",
  },
  "functions/internal": {
    summary: "error.functions.internal.summary",
    detail: "error.functions.internal.detail",
  },
  "functions/unavailable": {
    summary: "error.functions.unavailable.summary",
    detail: "error.functions.unavailable.detail",
  },
  "functions/unauthenticated": {
    summary: "error.functions.unauthenticated.summary",
    detail: "error.functions.unauthenticated.detail",
  },
  "functions/already-exists": {
    summary: "error.functions.already_exists.summary",
    detail: "error.functions.already_exists.detail",
  },
  "functions/not-found": {
    summary: "error.functions.not_found.summary",
    detail: "error.functions.not_found.detail",
  },
  "functions/aborted": {
    summary: "error.functions.aborted.summary",
    detail: "error.functions.aborted.detail",
  },
  "functions/failed-precondition": {
    summary: "error.functions.failed_precondition.summary",
    detail: "error.functions.failed_precondition.detail",
  },
  "functions/resource-exhausted": {
    summary: "error.functions.resource_exhausted.summary",
    detail: "error.functions.resource_exhausted.detail",
  },
};

const FIRESTORE_ERROR_MAP: Record<string, ErrorResult> = {
  "permission-denied": {
    summary: "error.firestore.permission_denied.summary",
    detail: "error.firestore.permission_denied.detail",
  },
  "not-found": {
    summary: "error.firestore.not_found.summary",
    detail: "error.firestore.not_found.detail",
  },
  unavailable: {
    summary: "error.firestore.unavailable.summary",
    detail: "error.firestore.unavailable.detail",
  },
  unauthenticated: {
    summary: "error.firestore.unauthenticated.summary",
    detail: "error.firestore.unauthenticated.detail",
  },
  "failed-precondition": {
    summary: "error.firestore.failed_precondition.summary",
    detail: "error.firestore.failed_precondition.detail",
  },
  aborted: {
    summary: "error.firestore.aborted.summary",
    detail: "error.firestore.aborted.detail",
  },
  "already-exists": {
    summary: "error.firestore.already_exists.summary",
    detail: "error.firestore.already_exists.detail",
  },
};

const STORAGE_ERROR_MAP: Record<string, ErrorResult> = {
  "storage/unknown": {
    summary: "error.storage.unknown.summary",
    detail: "error.storage.unknown.detail",
  },
  "storage/unauthorized": {
    summary: "error.storage.unauthorized.summary",
    detail: "error.storage.unauthorized.detail",
  },
  "storage/object-not-found": {
    summary: "error.storage.object_not_found.summary",
    detail: "error.storage.object_not_found.detail",
  },
  "storage/quota-exceeded": {
    summary: "error.storage.quota_exceeded.summary",
    detail: "error.storage.quota_exceeded.detail",
  },
  "storage/unauthenticated": {
    summary: "error.storage.unauthenticated.summary",
    detail: "error.storage.unauthenticated.detail",
  },
  "storage/canceled": {
    summary: "error.storage.canceled.summary",
    detail: "error.storage.canceled.detail",
  },
  "storage/retry-limit-exceeded": {
    summary: "error.storage.retry_limit_exceeded.summary",
    detail: "error.storage.retry_limit_exceeded.detail",
  },
};

const SERVICE_MAPS: [string, Record<string, ErrorResult>][] = [
  ["auth/", AUTH_ERROR_MAP],
  ["functions/", FUNCTIONS_ERROR_MAP],
  ["storage/", STORAGE_ERROR_MAP],
];

const FILE_TOO_LARGE_PATTERN = /payload too large|file too large|too large|413/i;

const RESOURCE_WITH_SUMMARY_SUFFIX = "_with_resource";

// Resources named after the brand, so white-labelled customers see their own name
const BRANDED_RESOURCES: Record<string, keyof typeof PUBLIC_CONFIG_DEFAULTS.static> = {
  mimbox: "mimboxName",
};

const resolveResourceLabel = (resource?: string): string => {
  if (resource && resource in BRANDED_RESOURCES) {
    const key = BRANDED_RESOURCES[resource];
    return useAuthStore().entity.config.getStatic(key, {
      default: PUBLIC_CONFIG_DEFAULTS.static[key],
    });
  }
  if (resource) {
    const key = `error.resources.${resource}`;
    return i18n.global.te(key) ? i18n.global.t(key) : resource;
  }
  return i18n.global.t("error.resources.generic");
};

const applyResourceToSummary = (summaryKey: string): string => {
  const resourceKey = `${summaryKey}${RESOURCE_WITH_SUMMARY_SUFFIX}`;
  return i18n.global.te(resourceKey) ? resourceKey : summaryKey;
};

const isFileTooLargeError = (error: unknown): boolean => {
  const storageError = error as {
    customData?: { serverResponse?: string | null };
    message?: string;
  };
  const payload = [storageError.customData?.serverResponse, storageError.message]
    .filter(Boolean)
    .join("\n");
  return FILE_TOO_LARGE_PATTERN.test(payload);
};

const errorCode = (error: unknown): string | undefined =>
  typeof error === "object" && error !== null && "code" in error
    ? String((error as { code: unknown }).code)
    : undefined;

export function formatError(error: unknown, options?: FormatErrorOptions): ErrorResult {
  const params = { resource: resolveResourceLabel(options?.resource) };

  const code = errorCode(error);
  if (code !== undefined) {
    if (code.startsWith("storage/") && isFileTooLargeError(error)) {
      return {
        summary: "error.storage.file_too_large.summary",
        detail: "error.storage.file_too_large.detail",
        params,
      };
    }

    for (const [prefix, map] of SERVICE_MAPS) {
      if (code.startsWith(prefix) && code in map) {
        const entry = map[code];
        return {
          summary: options?.resource ? applyResourceToSummary(entry.summary) : entry.summary,
          detail: entry.detail,
          params,
        };
      }
    }

    if (code in FIRESTORE_ERROR_MAP) {
      const entry = FIRESTORE_ERROR_MAP[code];
      return {
        summary: options?.resource ? applyResourceToSummary(entry.summary) : entry.summary,
        detail: entry.detail,
        params,
      };
    }
  }

  return { summary: "error.generic.summary", detail: "error.generic.detail" };
}
type ReportOnErrorOptions = {
  returnReport?: boolean;
  resource?: string;
};
export async function reportOnError<T>(
  promise: Promise<T>,
  opt: ReportOnErrorOptions & { returnReport: true },
): Promise<{ resolved: T; report?: ReturnType<typeof formatError> }>;
export async function reportOnError<T>(
  promise: Promise<T>,
  opt?: ReportOnErrorOptions & { returnReport?: false },
): Promise<T>;
export async function reportOnError<T>(
  promise: Promise<T>,
  opt: ReportOnErrorOptions = {},
): Promise<T | { resolved: T; report?: ReturnType<typeof formatError> }> {
  const toastStore = useToastStore();
  let thrownError: unknown;
  let report: ReturnType<typeof formatError> = {
    summary: "",
  };
  const resolved = await promise.catch((error) => {
    thrownError = error;
    const code = errorCode(error);
    debug.error("reportOnError", { resource: opt.resource, code, error });
    sentryClient?.captureException(error, {
      captureContext: { tags: { resource: opt.resource, code } },
    });
    try {
      report = formatError(error, { resource: opt.resource });
    } catch (formatterError) {
      // Never let a broken message hide the real error
      debug.error("formatError failed", formatterError);
      report = { summary: "error.generic.summary", detail: "error.generic.detail" };
    }
    return null as T;
  });
  if (opt.returnReport) {
    if (thrownError) {
      report.summary = i18n.global.t(report.summary, report.params ?? {});
      report.detail = report.detail ? i18n.global.t(report.detail, report.params ?? {}) : undefined;
      return {
        resolved,
        report,
      };
    }
    return {
      resolved,
    };
  }

  if (thrownError) {
    toastStore.error(report);
    throw thrownError;
  }
  return resolved;
}
